/**
 * Tema: claro, oscuro y automático.
 *
 * "Automático" no es un tercer color: es delegar en el sistema. Se marca
 * .sys-dark en <html> y el CSS decide (index.css). Escuchamos los cambios
 * del sistema en vivo — quien lleva el móvil en modo noche automático ve
 * cambiar la app al anochecer sin recargarla.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');

function apply(mode: ThemeMode) {
  const html = document.documentElement;
  const systemDark = darkQuery().matches;

  html.setAttribute('data-mode', mode);
  html.classList.toggle('sys-dark', systemDark);

  // Las pantallas todavía sin rediseñar usan `dark:` de Tailwind. Sin esta
  // clase seguirían al sistema en vez de al tema elegido, y verías media app
  // clara y media oscura. Se puede borrar cuando no quede ningún `dark:`.
  html.classList.toggle('dark', mode === 'dark' || (mode === 'auto' && systemDark));
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'auto',
      setMode: (mode) => {
        apply(mode);
        set({ mode });
      },
    }),
    {
      name: 'kakuat-theme',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        apply(state?.mode ?? 'auto');
      },
    }
  )
);

// El sistema puede cambiar con la app abierta (modo noche automático).
darkQuery().addEventListener('change', () => {
  apply(useThemeStore.getState().mode);
});
